import {
  GridItem,
  InverseHeader,
  LayoutGrid,
  Tabs,
  TypographyV3,
} from '@jouzen/ecom-components';
import { useTranslations } from 'next-intl';
import { useState } from 'react';

import BackdropBlur from '@/app/components/BackdropBlur';
import Image from '@/app/components/Image';
import Motion from '@/app/components/Motion';
import SectionHeading from '@/app/components/SectionHeading';

import MobileTabs from './MobileTabs';

const VARIANTS = {
  0: { height: 745 },
  1: { height: 629 },
  2: { height: 652 },
};

interface AppImage {
  alt: string;
  src: string;
}

const APP_UI_IMAGES: Record<number, AppImage> = {
  0: {
    alt: 'june_app_image_alt',
    src: 'june_app_ui',
  },
  1: {
    alt: 'july_app_image_alt',
    src: 'july_app_ui',
  },
  2: {
    alt: 'august_app_image_alt',
    src: 'august_app_ui',
  },
};

const WhyOuraPersonalInsights = (): JSX.Element => {
  const [activeTab, setActiveTab] = useState<number>(0);
  const t = useTranslations();
  const appImage = APP_UI_IMAGES[activeTab]!;

  return (
    <section
      className="bg-sandstone-200 py-16 text-sandstone-500 md:pb-0"
      data-cy="why-oura-personal-insights"
    >
      <LayoutGrid>
        <GridItem
          className="lg:pb-16"
          colEnd={{ sm: 'main', lg: 12 }}
          colStart={{ sm: 'main', lg: 'main' }}
        >
          <SectionHeading
            animated
            content="insights_title"
            emAnimationProps={{
              transition: { duration: 2, type: 'tween', delay: 0.5 },
            }}
            indentation="pl-14 lg:pl-36"
            multiline
            typographyProps={{
              className: 'leading-heading tracking-tighter',
              color: 'inherit',
              Element: 'h3',
              variant: 'h3',
            }}
            wrapperAnimationProps={{
              transition: { duration: 1.25, type: 'tween', delay: 0.25 },
            }}
          />
        </GridItem>
        <GridItem
          className="pb-12 pt-6 lg:pb-0"
          colEnd={{ sm: 'main', lg: 'main' }}
          colStart={{ sm: 'main', lg: 15 }}
        >
          <Motion
            initial={{ filter: 'blur(5px)', opacity: 0 }}
            whileInView={{ filter: 'blur(0px)', opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.5 }}
          >
            <TypographyV3 color="inherit">{t('insights_copy')}</TypographyV3>
          </Motion>
        </GridItem>
        <InverseHeader className="col-main flex justify-center md:hidden" enter>
          <MobileTabs />
        </InverseHeader>
        <InverseHeader
          className="col-full hidden justify-center overflow-hidden md:relative md:flex md:h-[900px]"
          enter
        >
          <Motion
            className="absolute bottom-28 z-10"
            data-cy="personal-insights-app-ui"
            initial={{ height: 745 }}
            animate={VARIANTS[activeTab as keyof typeof VARIANTS]}
          >
            <div className="relative h-full w-[361px] rounded-lg">
              <BackdropBlur color={{ sm: 'dark', lg: 'dark' }} variant="card" />
              <Image
                alt={t(appImage.alt)}
                className="relative max-h-[745px] w-full rounded-md"
                format="png"
                src={t(appImage.src)}
                width={361}
                height={745}
                priority
              />
            </div>
          </Motion>
          <Tabs
            inside
            inverted
            onChange={(index) => setActiveTab(index)}
            orientation="reverse"
            style="pill"
            tabs={[
              {
                label: t('june'),
              },
              {
                label: t('july'),
              },
              {
                label: t('august'),
              },
            ]}
            tabPanels={[
              {
                className: 'absolute inset-0 bg-sandstone-200',
                content: (
                  <Motion
                    className="size-full"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  >
                    <Image
                      alt={t('personal_insights_image_alt_1')}
                      fill
                      priority
                      className="object-cover"
                      src="blue-sky/why-oura/personal-insights-june-1"
                      sizes="100vw"
                    />
                  </Motion>
                ),
              },
              {
                className: 'absolute inset-0 bg-sandstone-200',
                content: (
                  <Motion
                    className="size-full"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  >
                    <Image
                      alt={t('personal_insights_image_alt_2')}
                      fill
                      className="object-cover"
                      src="blue-sky/why-oura/personal-insights-july-1"
                      sizes="100vw"
                    />
                  </Motion>
                ),
              },
              {
                className: 'absolute inset-0 bg-sandstone-200',
                content: (
                  <Motion
                    className="size-full"
                    initial={{ opacity: 0 }}
                    whileInView={{ opacity: 1 }}
                    transition={{ duration: 1 }}
                  >
                    <Image
                      alt={t('personal_insights_image_alt_3')}
                      fill
                      className="object-cover"
                      src="blue-sky/why-oura/personal-insights-august-1"
                      sizes="100vw"
                    />
                  </Motion>
                ),
              },
            ]}
          />
        </InverseHeader>
      </LayoutGrid>
    </section>
  );
};

export default WhyOuraPersonalInsights;
